import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core'
import { Router, Scroll, NavigationEnd, NavigationStart } from '@angular/router'
import { Observable, Subscription, interval, combineLatest, of, merge, Subject } from 'rxjs'
import { map, withLatestFrom, tap, startWith, distinctUntilChanged, filter, take, switchMap } from 'rxjs/operators'
import { SettingsService } from '../../../modules/settings'
import DateTimeUtils from '../../../utils/date-time.utils'
import { AuthFacadeService } from '../../services/auth-facade.service'
import { AuthService } from '../../services/auth.service'
import { EntityService, Field } from '../../../modules/dto'

/** This component provides a mechanism to periodically check, if the users token is still valid.
 *  If the token is not valid, it'll show a modal dialog forcing the user to reenter his password or to log out.
 *
 *  Open items:
 *
 *  - autofill in the password input does not trigger the ngmodel -> the button will be displayed as disabled.
 *
 *
 * todo:
 * - streams:
 * -  showDialog
 * -   user is not active/seesion is about to expire
 * -   interval-source
 * -   session is not yet expired
 * -  navToLogin/resetCompleteState
 * -   user is active and session is expired
 * -   application start
 * -   no showDialog
 * -  validate/extend token
 * -   user is active
 * -   interval-source
 * -   session is not yet expired
 */
@Component({
  selector: 'jml-lib-reactivate-token-dialog',
  templateUrl: './reactivate-token-dialog.component.html',
  styleUrls: ['./reactivate-token-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReactivateTokenDialogComponent implements OnInit, OnDestroy {
  /** minutes between showing the dialog and the token actually expiring. */
  readonly TOKEN_EXPIRE_DISTANCE_IN_MINUTES = 1
  /** provides an impulse to react to (will be set to 1 minute).*/
  intervalSource$: Observable<number>
  /** combines multiple observable and determines whether to show the modal session-expired dialog. */
  // showDialog$: Observable<boolean>;
  /** determines, whether the token is less than TOKEN_EXPIRE_DISTANCE_IN_MINUTES away from expiring. */
  tokenIsAboutToExpire$: Observable<boolean>
  /** determines whether the user currently is logged in. */
  userIsLoggedIn$: Observable<boolean>

  userName: string
  password: string

  /** determines whether a login/reactivation of the session is in progress. */
  loginActive$: Observable<boolean>

  sessionText = 'Ihre Sitzung ist aufgrund von Inaktivität abgelaufen.'

  subscriptions: Subscription[] = []

  // reference to unlock button to set the focus
  @ViewChild('unlock', { static: false }) unlockButton: ElementRef

  @ViewChild('passwordTextField', { static: false }) passwordTextField: ElementRef

  private urlContainsLogin$: Observable<boolean>
  private $triggerProlong: Subject<void> = new Subject<void>()
  showDialog$: Observable<boolean>

  constructor(
    public authenticationService: AuthService,
    private router: Router,
    private authFacade: AuthFacadeService,
    private settingsService: SettingsService,
    private entityService: EntityService
  ) {}

  ngOnInit() {
    // set interval to fire once per minute
    this.intervalSource$ = interval(60 * 1000).pipe(
      // starts directly (not waiting for 1 min to actually start)
      startWith(0)
      // tap(_ => console.log('interval fires'))
    )

    // is token expiration less than TOKEN_EXPIRE_DISTANCE_IN_MINUTES minutes away from now?
    this.tokenIsAboutToExpire$ = merge(
      this.intervalSource$,
      this.authFacade.userProfileUser$ // to react to changes in user profile (e.g. after login via session dialog)
        .pipe
        // tap(userProfile => console.log('userProfile changed: ', userProfile))
        ()
    ).pipe(
      // tap(e => console.log('tokenIsAboutToExpire', e)),
      withLatestFrom(this.authFacade.tokenValidUntil$),
      map(([_, tokenValidUntil]) => tokenValidUntil),
      // tap(tokenValidUntil => console.log('token valid until: ', tokenValidUntil)),
      map(
        (tokenValidUntil) =>
          !!tokenValidUntil &&
          DateTimeUtils.timeStampIsFurtherFromNowThanGivenMinutes(
            tokenValidUntil,
            this.TOKEN_EXPIRE_DISTANCE_IN_MINUTES
          )
      )
      // tap(tokenIsAboutToExpire => console.log('tokenIsAboutToExpire', tokenIsAboutToExpire))
    )

    this.userIsLoggedIn$ = this.authFacade.userIsLoggedIn$
    this.loginActive$ = this.authFacade.isInLogin$

    this.urlContainsLogin$ = this.router.events.pipe(
      map((routerEvent) => {
        let url = ''
        const scroll = routerEvent as Scroll
        if (!!scroll) {
          routerEvent = scroll.routerEvent
        }
        if (routerEvent instanceof NavigationEnd) {
          url = (routerEvent as NavigationEnd).url
        } else if (routerEvent instanceof NavigationStart) {
          url = (routerEvent as NavigationStart).url
        }
        return url.includes('/login')
      })
    )

    this.showDialog$ = combineLatest([this.intervalSource$, this.tokenIsAboutToExpire$]).pipe(
      map(([_, tokenIsAboutToExpire]) => tokenIsAboutToExpire),
      withLatestFrom(this.userIsLoggedIn$, this.urlContainsLogin$),
      map(([tokenIsAboutToExpire, isUserAuthenticated, urlIsLogin]) => {
        // console.log(`tokenIsAboutToExpire: ${tokenIsAboutToExpire},isUserAuthenticated: ${isUserAuthenticated},urlIsLogin: ${urlIsLogin},`);
        return tokenIsAboutToExpire && isUserAuthenticated && !urlIsLogin
      }),
      tap((showDialog) => {
        if (showDialog) {
          setTimeout(() => this.unlockButton.nativeElement.focus(), 50)
        }
      })
    )

    this.subscriptions.push(
      // track user activity and prolong token while user is active
      merge(this.intervalSource$, this.$triggerProlong)
        .pipe(
          withLatestFrom(
            this.authFacade.userIsCurrentlyActive$,
            this.showDialog$,
            this.userIsLoggedIn$,
            this.urlContainsLogin$
          ),
          map(
            ([_, isActive, showDialog, userIsLoggedIn, urlContainsLogin]) =>
              isActive && !showDialog && userIsLoggedIn && !urlContainsLogin
          ),
          filter((isActiveAndLoggedInAndNotShowDialog) => isActiveAndLoggedInAndNotShowDialog),

          tap((_) => console.log('prolonging user session')),
          tap((_) => this.authFacade.prolongToken())
        )
        .subscribe()
    )

    this.subscriptions.push(
      // validate token if user is inactive and token is still valid (no showDialog yet)
      this.intervalSource$
        .pipe(
          withLatestFrom(
            this.authFacade.userIsCurrentlyActive$,
            this.showDialog$,
            this.userIsLoggedIn$,
            this.urlContainsLogin$
          ),
          map(
            ([_, isActive, showDialog, userIsLoggedIn, urlContainsLogin]) =>
              !isActive && !showDialog && userIsLoggedIn && !urlContainsLogin
          ),
          filter((validateToken) => validateToken)
        )
        .subscribe((_) => {
          this.authFacade.validateToken()
        })
    )

    // set user name to input model
    this.subscriptions.push(
      this.authFacade.userProfileUser$
        .pipe(map((user) => user?.user?.loginName))
        .subscribe((userName) => (this.userName = userName))
    )

    // alter the dialog text to show the user the idle timeout setting
    this.subscriptions.push(
      this.authFacade.tokenIsNotYetExpired$
        .pipe(
          // ensure the user is logged in and the token is valid before trying to read a setting
          filter((tokenIsNotYetExpired) => tokenIsNotYetExpired),
          // for the moment this should be sufficient.
          take(1),
          // get USER_IDLE_SESSION_TIMEOUT
          switchMap((_) => this.settingsService.getUserIdleSessionTimeoutInMinutes()),
          // alter the text
          tap((idleTimeInMinutes) => {
            if (!!idleTimeInMinutes) {
              this.sessionText = `Ihre Sitzung ist aufgrund von Inaktivität (${idleTimeInMinutes} Minuten) abgelaufen.`
              // console.log('new session timeout text: ', this.sessionText);
            } else {
              // console.log('could not get value for USER_IDLE_SESSION_TIMEOUT');
            }
          })
        )
        .subscribe()
    )
  }

  ngOnDestroy(): void {
    // console.log('rtd-ngOnDestroy');
    this.subscriptions.forEach((sub) => sub.unsubscribe())
  }

  login(): void {
    this.authFacade.reactivateToken(this.userName, this.password)
    this.password = ''
  }

  logout() {
    this.authFacade.logout()
  }
}
